import React, { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import { colors, typography } from '../../theme';
import { useEditorCanvas } from '../../canvas/hooks/use-editor-canvas';
import { usePieceDispatch } from '../../model/piece-context';
import { useSelectedLayer, useSelectedLayerId } from '../hooks/use-selected-layer';
import { Fill, fromFabricFill, toFabricFill } from '../../shared/utils/fill';
import { ToolbarSeparator } from '../../shared/ui/toolbar-separator';
import { FillControl } from '../../shared/ui/fill-control';
import { StrokeControls } from '../../shared/ui/stroke-controls';
import { AnimationSelector } from '../../animations/components/animation-selector';
import { ToolBtn } from '../../shared/ui/tool-btn';
import { ShapeContent } from '../../model/types';

type PaintTarget = 'fill' | 'stroke';

const sliderLabelStyle: React.CSSProperties = {
  fontSize: typography.fontSize.base,
  fontFamily: typography.fontFamily.ui,
  color: colors.textMuted,
  flexShrink: 0
};
const sliderValueStyle: React.CSSProperties = {
  fontSize: typography.fontSize.base,
  fontFamily: typography.fontFamily.ui,
  color: colors.textSecondary,
  minWidth: '28px'
};

export const ShapeSettings: React.FC = () => {
  const { canvas, selectedObject, deleteSelected } = useEditorCanvas();
  const dispatch = usePieceDispatch();
  const layerId = useSelectedLayerId();
  const layer = useSelectedLayer();

  const [fill, setFill] = useState<Fill>({ kind: 'solid', color: '#3b82f6' });
  const [stroke, setStroke] = useState<Fill>({ kind: 'solid', color: '#1e40af' });
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [cornerRadius, setCornerRadius] = useState(0);

  // Sync the controls from the model.
  useEffect(() => {
    if (layer?.kind !== 'shape') return;
    const c = layer.content;
    setFill(c.fill);
    setStroke(fromFabricFill(c.stroke, '#1e40af'));
    setStrokeWidth(c.strokeWidth ?? 2);
    setCornerRadius(c.shape === 'rect' ? (c.cornerRadius ?? 0) : 0);
  }, [layer]);

  if (!canvas || !selectedObject || layer?.kind !== 'shape') return null;

  const c = layer.content;
  const isRect = c.shape === 'rect';
  const cornerMax = c.shape === 'rect' ? Math.max(1, Math.round(Math.min(c.width, c.height) / 2)) : 1;

  // Commit writes to the model (undoable).
  const patch = (p: Partial<ShapeContent>) => {
    if (layerId) dispatch({ type: 'patchLayerContent', id: layerId, patch: p });
  };

  // Preview updates Fabric only, until commit.
  const previewFiller = (key: PaintTarget, value: Fill) => {
    selectedObject.set({ [key]: toFabricFill(value) });
    canvas.requestRenderAll();
  };

  const commitFiller = (key: PaintTarget, value: Fill) => {
    (key === 'fill' ? setFill : setStroke)(value);
    previewFiller(key, value);
    patch(key === 'fill' ? { fill: value } : { stroke: value.kind === 'solid' ? value.color : '#000000' });
  };

  const previewStrokeWidth = (value: number) => {
    setStrokeWidth(value);
    selectedObject.set({ strokeWidth: value });
    canvas.requestRenderAll();
  };

  const previewCornerRadius = (value: number) => {
    setCornerRadius(value);
    selectedObject.set({ rx: value, ry: value });
    canvas.requestRenderAll();
  };

  return (
    <>
      <FillControl
        value={fill}
        onPreview={value => previewFiller('fill', value)}
        onCommit={value => commitFiller('fill', value)}
        label='Relleno'
      />

      <StrokeControls
        fill={stroke}
        width={strokeWidth}
        onPreviewFill={value => previewFiller('stroke', value)}
        onCommitFill={value => commitFiller('stroke', value)}
        onPreviewWidth={previewStrokeWidth}
        onCommitWidth={() => patch({ strokeWidth })}
      />

      {isRect && (
        <>
          <ToolbarSeparator />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={sliderLabelStyle}>Esquinas</span>
            <input
              type='range'
              min={0}
              max={cornerMax}
              step={1}
              value={cornerRadius}
              onChange={e => previewCornerRadius(Number(e.target.value))}
              onPointerUp={() => patch({ cornerRadius })}
              style={{ width: '80px', cursor: 'pointer' }}
            />
            <span style={sliderValueStyle}>{cornerRadius}px</span>
          </div>
        </>
      )}

      <ToolbarSeparator />

      <AnimationSelector />

      <ToolbarSeparator />

      <ToolBtn onClick={deleteSelected} variant='danger' title='Eliminar'>
        <Trash2 size={15} />
      </ToolBtn>
    </>
  );
};
